import { z } from "zod";
import { TIPOS_DE_FIGURINHA } from "./enums.js";
/**
 * A biblioteca de figurinhas, logos e artes de letras: os limites do upload, os pedidos do tRPC e a forma
 * da resposta.
 *
 * Os limites moram aqui, e não só no servidor, porque a tela os mostra antes de o arquivo sair do celular
 * ("PNG ou WebP, até 5 MB") e a recusa do servidor tem de dizer a mesma coisa.
 */
/** Só formatos com canal alfa: JPEG não tem transparência, e uma figurinha com fundo não é figurinha. */
export const FIGURINHA_TIPOS_DE_ARQUIVO = ["image/png", "image/webp"];
export const FIGURINHA_MAX_BYTES = 5 * 1024 * 1024;
/** O maior lado depois de normalizada. Sem ampliar: uma imagem menor fica como está. */
export const FIGURINHA_LADO_MAXIMO = 2048;
/**
 * O teto de pixels que o servidor aceita DECODIFICAR. Um PNG de poucos MB pode declarar 16 mil por 16 mil
 * pixels (uma "bomba de descompressão": centenas de MB em memória), e o limite de bytes não vê isso.
 */
export const FIGURINHA_PIXELS_MAXIMOS = 36_000_000;
/**
 * A parte mínima da imagem que tem de ser transparente (alfa abaixo de `FIGURINHA_ALFA_OPACO`) para ela
 * valer como "recortada". Não é zero porque um PNG exportado de um JPEG, ou de um editor que "tem alfa"
 * sem usá-lo, costuma trazer o canal inteiro opaco com um pixel perdido de fora — e não é um recorte.
 * 0,1% deixa passar cantos arredondados bem discretos.
 */
export const FIGURINHA_FRACAO_MINIMA_TRANSPARENTE = 0.001;
/** Um pixel com alfa abaixo disto (de 255) conta como transparente. */
export const FIGURINHA_ALFA_OPACO = 250;
export const FIGURINHA_NOME_MAX = 80;
export const FIGURINHA_DESCRICAO_MAX = 1000;
/** A frase da recusa, a mesma para quem sobe uma imagem sem canal alfa e para uma 100% opaca. */
export const MENSAGEM_DE_FIGURINHA_SEM_TRANSPARENCIA = "A imagem precisa ter fundo transparente (PNG ou WebP). Esta não tem: envie uma imagem já recortada, sem o fundo.";
export const tipoDeFigurinhaSchema = z.enum(TIPOS_DE_FIGURINHA, "Escolha o tipo da imagem: figurinha, logo ou texto");
/**
 * Os campos de texto do upload (multipart: tudo chega como texto). `nome` vazio é "sem nome": o servidor
 * usa o nome do arquivo.
 */
export const uploadDeFigurinhaSchema = z.object({
    tipo: tipoDeFigurinhaSchema,
    nome: z
        .string()
        .trim()
        .max(FIGURINHA_NOME_MAX, `O nome tem no máximo ${FIGURINHA_NOME_MAX} letras`)
        .optional()
        .transform((n) => (n === undefined || n === "" ? undefined : n)),
});
const idDaFigurinha = z.number().int().positive();
export const figurinhaPorIdSchema = z.object({ id: idDaFigurinha });
export const listarFigurinhasSchema = z
    .object({
    tipo: tipoDeFigurinhaSchema.optional(),
    /** Parte do nome ou da descrição, sem distinguir maiúsculas. */
    busca: z.string().trim().max(100).optional(),
    limite: z.number().int().min(1).max(500).default(300),
})
    .default({ limite: 300 });
/**
 * Editar uma imagem da biblioteca. `publica` só vale para o admin (o servidor recusa o resto), e
 * `descricao: null` (ou vazia) apaga a descrição. Ao menos um campo, para não haver chamada que não muda nada.
 */
export const atualizarFigurinhaSchema = z
    .object({
    id: idDaFigurinha,
    nome: z.string().trim().min(1, "Dê um nome à imagem").max(FIGURINHA_NOME_MAX).optional(),
    descricao: z
        .string()
        .trim()
        .max(FIGURINHA_DESCRICAO_MAX, `A descrição tem no máximo ${FIGURINHA_DESCRICAO_MAX} letras`)
        .nullable()
        .optional(),
    publica: z.boolean().optional(),
})
    .refine((v) => v.nome !== undefined || v.descricao !== undefined || v.publica !== undefined, "Nada a alterar");
//# sourceMappingURL=figurinhas.js.map